import { CheckIcon, SearchIcon } from 'lucide-react'
import { useState } from 'react'

import { cn, numberToCurrency } from '@/utils'
import {
  GROUP_PAST,
  groupByInstitution,
  matchesSearch,
} from '../AccountsTable/accountLines'
import type { AccountAsOf } from '../model/portfolioOverview'
import { AccountCount } from './AccountCount'

interface AccountMenuProps {
  accounts: AccountAsOf[]
  selectedAccountId: string | null
  onChoose: (accountId: string | null) => void
}

const OPTION_CLASS =
  'flex w-full cursor-pointer items-center justify-between gap-4 rounded-md px-2.5 py-[7px] text-left text-sm hover:bg-slate-100'

const accountDetail = (account: AccountAsOf) =>
  account.value == null
    ? account.accountType
    : `${account.accountType} · ${numberToCurrency(account.value)}`

// Type to search; results are grouped by institution and show each account's type and value.
export const AccountMenu = ({
  accounts,
  selectedAccountId,
  onChoose,
}: AccountMenuProps) => {
  const [search, setSearch] = useState('')
  const groups = groupByInstitution(
    accounts.filter((account) => matchesSearch(account, search)),
  )

  return (
    <div className="text-blue-7">
      {accounts.length > GROUP_PAST && (
        <label className="mb-1.5 flex h-9 items-center gap-2 rounded-md border px-2.5">
          <SearchIcon className="size-4 text-blue-5" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${accounts.length} accounts by name, number or type`}
            className="w-full bg-transparent text-sm outline-none placeholder:text-blue-4"
          />
        </label>
      )}
      <div className="max-h-[330px] overflow-y-auto">
        <button
          type="button"
          onClick={() => onChoose(null)}
          className={cn(
            OPTION_CLASS,
            selectedAccountId == null && 'bg-slate-100',
          )}
        >
          <span className="flex items-center gap-2">
            All accounts
            <AccountCount count={accounts.length} />
          </span>
          {selectedAccountId == null && <CheckIcon className="size-4" />}
        </button>
        {groups.map((group) => (
          <div key={group.institution}>
            <div className="px-2.5 pt-2.5 pb-1 text-xs text-ink-subtle">
              {group.institution}
            </div>
            {group.accounts.map((account) => (
              <button
                key={account.accountId}
                type="button"
                onClick={() => onChoose(account.accountId)}
                className={cn(
                  OPTION_CLASS,
                  account.accountId === selectedAccountId && 'bg-slate-100',
                )}
              >
                <span>{account.name}</span>
                <span className="text-xs tabular-nums text-ink-subtle">
                  {accountDetail(account)}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
